import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, getDocs, query, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { parseSantanderStatementText, parseSantanderCSV, categorizeMovimiento, CATEGORIAS } from '../../utils/parseBankStatement';
import { sugerirCoincidencias, MOTIVO_LABEL } from '../../utils/conciliacionMatch';
import { authService } from '../../services/authService';
import BancoDashboard from './BancoDashboard';

// ── Utilidades ────────────────────────────────────────────────────────────────

function Spinner() {
  return <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto" />;
}

const fmt = (n) => {
  if (n == null) return '—';
  const num = Number(n);
  return (num < 0 ? '-' : '+') + Math.abs(num).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const fmtAbs = (n) => n != null
  ? Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
  : '—';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-ES'); } catch { return d; }
};

// Parsear CSV de banco (formato común ES: fecha;concepto;importe;saldo)
// Soporta delimitadores , y ; y distintos formatos de fecha (dd/mm/yyyy, yyyy-mm-dd)
function parseBankCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  // Detectar delimitador
  const delim = lines[0].includes(';') ? ';' : ',';

  // Intentar detectar columnas en la primera línea
  const headers = lines[0].split(delim).map(h => h.trim().toLowerCase().replace(/["""]/g, ''));

  const colMap = {
    fecha: headers.findIndex(h => h.includes('fecha') || h.includes('date')),
    concepto: headers.findIndex(h => h.includes('concepto') || h.includes('descripci') || h.includes('movimiento') || h.includes('operaci')),
    importe: headers.findIndex(h => h.includes('importe') || h.includes('amount') || h.includes('cargo') || h.includes('abono') || h.includes('valor')),
    saldo: headers.findIndex(h => h.includes('saldo') || h.includes('balance')),
  };

  // Si no detecta cabeceras, asumir col 0=fecha, 1=concepto, 2=importe, 3=saldo
  if (colMap.fecha === -1) colMap.fecha = 0;
  if (colMap.concepto === -1) colMap.concepto = 1;
  if (colMap.importe === -1) colMap.importe = 2;
  if (colMap.saldo === -1) colMap.saldo = 3;

  const parseImporte = (s) => {
    if (!s) return null;
    // Eliminar espacios, comillas, símbolo €
    let clean = String(s).replace(/["""€\s]/g, '');
    // Formato ES: 1.234,56 → usar el último separador como decimal
    if (clean.match(/,\d{2}$/)) {
      clean = clean.replace(/\./g, '').replace(',', '.');
    }
    const n = parseFloat(clean);
    return isNaN(n) ? null : n;
  };

  const parseDate = (s) => {
    if (!s) return null;
    const str = String(s).replace(/["""]/g, '').trim();
    // dd/mm/yyyy o dd-mm-yyyy
    const es = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (es) return `${es[3]}-${es[2].padStart(2, '0')}-${es[1].padStart(2, '0')}`;
    // yyyy-mm-dd
    const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return iso[0];
    return null;
  };

  const movimientos = [];
  const dataLines = headers.some(h => h.includes('fecha') || h.includes('date')) ? lines.slice(1) : lines;

  for (const line of dataLines) {
    if (!line.trim()) continue;
    const cols = line.split(delim).map(c => c.replace(/^[""]|[""]$/g, '').trim());
    const fecha = parseDate(cols[colMap.fecha]);
    const concepto = cols[colMap.concepto] || '';
    const importe = parseImporte(cols[colMap.importe]);
    const saldo = colMap.saldo >= 0 ? parseImporte(cols[colMap.saldo]) : null;

    if (!fecha && importe === null) continue;

    movimientos.push({
      fecha: fecha || cols[colMap.fecha],
      concepto: concepto || '—',
      importe: importe ?? 0,
      saldo,
      conciliado: false,
      facturaId: null,
      facturaRef: null,
    });
  }

  return movimientos;
}

// ── Extraer texto de un PDF (extracto bancario) en el navegador ────────────────
// pdf.js no da directamente "líneas" — da cada trozo de texto con su posición
// (x,y). Se agrupan los trozos que comparten Y (misma línea visual) y se
// ordenan por X dentro de cada línea, reconstruyendo así el texto en el mismo
// orden de lectura que si se copiara a mano desde el visor de PDF — que es
// justo el formato que espera parseSantanderStatementText().
async function extractTextFromPdf(arrayBuffer) {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

  const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  let fullText = '';
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const rows = new Map();
    for (const item of content.items) {
      if (!item.str || !item.str.trim()) continue;
      const y = Math.round(item.transform[5]);
      if (!rows.has(y)) rows.set(y, []);
      rows.get(y).push(item);
    }
    const sortedYs = [...rows.keys()].sort((a, b) => b - a); // de arriba (Y grande) a abajo
    for (const y of sortedYs) {
      const line = rows.get(y).sort((a, b) => a.transform[4] - b.transform[4]).map(it => it.str).join(' ');
      fullText += line + '\n';
    }
  }
  return fullText;
}

// ── Estado de conciliación ────────────────────────────────────────────────────

const ESTADO_CONC = {
  conciliado:   { label: 'Conciliado',   color: 'bg-green-100 text-green-700' },
  parcial:      { label: 'Parcial',      color: 'bg-yellow-100 text-yellow-700' },
  no_conciliado:{ label: 'Sin conciliar', color: 'bg-gray-100 text-gray-500' },
};

// ── Panel de importación CSV ──────────────────────────────────────────────────

function ImportCSVPanel({ onImport }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState([]);
  const [error, setError] = useState('');
  const [rawMovs, setRawMovs] = useState([]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const movs = parseBankCSV(text);
        if (movs.length === 0) {
          setError('No se encontraron movimientos en el archivo. Comprueba el formato.');
          return;
        }
        setRawMovs(movs);
        setPreview(movs.slice(0, 5));
      } catch (err) {
        setError(`Error al leer el archivo: ${err.message}`);
      }
    };
    reader.readAsText(file, 'ISO-8859-1');
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-red-300 transition-colors">
        <div className="text-5xl mb-3">📂</div>
        <p className="text-gray-600 font-medium mb-1">Importar extracto bancario en CSV</p>
        <p className="text-xs text-gray-400 mb-4">
          Formatos soportados: CSV de BBVA, Sabadell, Santander, CaixaBank, ING y similares.<br />
          Columnas detectadas automáticamente: Fecha · Concepto · Importe · Saldo
        </p>
        <button
          onClick={() => fileRef.current?.click()}
          className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
        >
          Seleccionar archivo CSV
        </button>
        <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
      </div>

      {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}

      {preview.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            Vista previa — {rawMovs.length} movimientos detectados
          </p>
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Fecha', 'Concepto', 'Importe', 'Saldo'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-bold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((m, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="px-3 py-2 text-gray-500">{fmtDate(m.fecha)}</td>
                    <td className="px-3 py-2 text-gray-800 max-w-[250px] truncate">{m.concepto}</td>
                    <td className={`px-3 py-2 font-semibold ${m.importe >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmt(m.importe)}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{m.saldo != null ? fmtAbs(m.saldo) : '—'}</td>
                  </tr>
                ))}
                {rawMovs.length > 5 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-center text-gray-400 text-xs">
                      … y {rawMovs.length - 5} movimientos más
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => onImport(rawMovs)}
              className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
            >
              Importar {rawMovs.length} movimientos
            </button>
            <button
              onClick={() => { setPreview([]); setRawMovs([]); setError(''); if (fileRef.current) fileRef.current.value = ''; }}
              className="px-5 py-2 border border-gray-200 text-sm font-semibold rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panel de importación CSV del Santander ──────────────────────────────────────
// Más fiable que el PDF: es una tabla real, sin depender del orden en que un
// lector de PDF va soltando el texto. Es la opción recomendada por defecto.
function ImportSantanderCSVPanel({ onImport }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState([]);
  const [rawMovs, setRawMovs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setLoading(true); setPreview([]); setRawMovs([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const movs = parseSantanderCSV(ev.target.result);
        if (movs.length === 0) {
          setError('No se ha reconocido ningún movimiento en este archivo. Comprueba que es el CSV de "Movimientos de Cuenta" del Santander.');
          return;
        }
        setRawMovs(movs);
        setPreview(movs.slice(0, 5));
      } catch (err) {
        setError(`No se ha podido leer el archivo: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    reader.onerror = () => { setError('No se ha podido leer el archivo.'); setLoading(false); };
    // Los CSV que exporta la banca online del Santander vienen en ISO-8859-1,
    // no en UTF-8 — leerlo como UTF-8 (file.text()) corrompe los acentos
    // ("Operación" → "OperaciÃ³n") y la cabecera deja de reconocerse.
    reader.readAsText(file, 'ISO-8859-1');
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-red-300 transition-colors">
        <div className="text-5xl mb-3">📄</div>
        <p className="text-gray-600 font-medium mb-1">Extracto de movimientos en CSV (Santander)</p>
        <p className="text-xs text-gray-400 mb-4">
          El CSV que descargas desde la banca online — "Movimientos de Cuenta". Es la opción más fiable, léelo si puedes elegir formato al exportar.
        </p>
        <button onClick={() => fileRef.current?.click()} disabled={loading}
          className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50">
          {loading ? 'Leyendo…' : 'Seleccionar CSV'}
        </button>
        <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
      </div>

      {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}

      {preview.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            Vista previa — {rawMovs.length} movimientos detectados
          </p>
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Fecha', 'Concepto', 'Importe', 'Saldo', 'Categoría'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-bold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((m, i) => {
                  const cat = categorizeMovimiento(m.concepto, m.importe);
                  return (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="px-3 py-2 text-gray-500">{fmtDate(m.fecha)}</td>
                      <td className="px-3 py-2 text-gray-800 max-w-[220px] truncate">{m.concepto}</td>
                      <td className={`px-3 py-2 font-semibold ${m.importe >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(m.importe)}</td>
                      <td className="px-3 py-2 text-gray-500">{m.saldo != null ? fmtAbs(m.saldo) : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{cat.label}</td>
                    </tr>
                  );
                })}
                {rawMovs.length > 5 && (
                  <tr><td colSpan={5} className="px-3 py-2 text-center text-gray-400 text-xs">… y {rawMovs.length - 5} movimientos más</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3">
            <button onClick={() => onImport(rawMovs)} className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700">
              Importar {rawMovs.length} movimientos
            </button>
            <button
              onClick={() => { setPreview([]); setRawMovs([]); setError(''); if (fileRef.current) fileRef.current.value = ''; }}
              className="px-5 py-2 border border-gray-200 text-sm font-semibold rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panel de importación PDF (extracto de movimientos del banco) ───────────────
function ImportPDFPanel({ onImport }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState([]);
  const [rawMovs, setRawMovs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [modo, setModo] = useState('pdf'); // 'pdf' | 'pegar'
  const [pegado, setPegado] = useState('');

  const procesarTexto = (text) => {
    const movs = parseSantanderStatementText(text);
    if (movs.length === 0) {
      setError('No se ha reconocido ningún movimiento en el texto. Si has subido un PDF, prueba a abrirlo, seleccionar todo el texto (Ctrl+A) y pegarlo con la otra opción.');
      setRawMovs([]); setPreview([]);
      return;
    }
    setRawMovs(movs);
    setPreview(movs.slice(0, 5));
    setError('');
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setLoading(true); setPreview([]); setRawMovs([]);
    try {
      const buffer = await file.arrayBuffer();
      const text = await extractTextFromPdf(buffer);
      procesarTexto(text);
    } catch (err) {
      setError(`No se ha podido leer el PDF: ${err.message}. Prueba a pegar el texto directamente.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-100">
        {[['pdf', 'Subir PDF'], ['pegar', 'Pegar texto']].map(([id, label]) => (
          <button key={id} onClick={() => { setModo(id); setError(''); }}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              modo === id ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {modo === 'pdf' ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-red-300 transition-colors">
          <div className="text-5xl mb-3">🏦</div>
          <p className="text-gray-600 font-medium mb-1">Extracto de movimientos en PDF (Santander)</p>
          <p className="text-xs text-gray-400 mb-4">
            El PDF que descargas desde la banca online — "Movimientos de Cuenta".<br />
            Si algún importe sale mal leído, prueba la pestaña "Pegar texto".
          </p>
          <button onClick={() => fileRef.current?.click()} disabled={loading}
            className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50">
            {loading ? 'Leyendo PDF…' : 'Seleccionar PDF'}
          </button>
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handleFile} />
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Abre el PDF, pulsa Ctrl+A (seleccionar todo) y Ctrl+C (copiar), y pega aquí el resultado.
          </p>
          <textarea
            value={pegado}
            onChange={e => setPegado(e.target.value)}
            rows={6}
            placeholder="Pega aquí el texto del extracto…"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-red-400"
          />
          <button onClick={() => procesarTexto(pegado)} disabled={!pegado.trim()}
            className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-40">
            Analizar texto
          </button>
        </div>
      )}

      {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}

      {preview.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            Vista previa — {rawMovs.length} movimientos detectados
          </p>
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Fecha', 'Concepto', 'Importe', 'Saldo', 'Categoría'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-bold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((m, i) => {
                  const cat = categorizeMovimiento(m.concepto, m.importe);
                  return (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="px-3 py-2 text-gray-500">{fmtDate(m.fecha)}</td>
                      <td className="px-3 py-2 text-gray-800 max-w-[220px] truncate">{m.concepto}</td>
                      <td className={`px-3 py-2 font-semibold ${m.importe >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(m.importe)}</td>
                      <td className="px-3 py-2 text-gray-500">{m.saldo != null ? fmtAbs(m.saldo) : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{cat.label}</td>
                    </tr>
                  );
                })}
                {rawMovs.length > 5 && (
                  <tr><td colSpan={5} className="px-3 py-2 text-center text-gray-400 text-xs">… y {rawMovs.length - 5} movimientos más</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3">
            <button onClick={() => onImport(rawMovs)} className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700">
              Importar {rawMovs.length} movimientos
            </button>
            <button
              onClick={() => { setPreview([]); setRawMovs([]); setError(''); setPegado(''); if (fileRef.current) fileRef.current.value = ''; }}
              className="px-5 py-2 border border-gray-200 text-sm font-semibold rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Fila de movimiento con conciliación manual ────────────────────────────────

function MovRow({ mov, facturas, onConciliar }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState('');

  // Sugerencias compartidas con la pestaña Facturas (conciliacionMatch.js) —
  // antes solo miraba importe±5%+fecha; ahora también admite una coincidencia
  // fuerte del nombre del cliente en el concepto aunque el importe no cuadre.
  const facturasMatch = useMemo(() => sugerirCoincidencias(mov, facturas), [mov, facturas]);

  const estadoConc = mov.conciliado ? 'conciliado' : 'no_conciliado';
  const ei = ESTADO_CONC[estadoConc];

  return (
    <>
      <tr className={`border-b border-gray-50 ${mov.conciliado ? 'bg-green-50/30' : 'hover:bg-gray-50'} cursor-pointer`} onClick={() => setExpanded(e => !e)}>
        <td className="py-2.5 px-3 text-sm text-gray-500">{fmtDate(mov.fecha)}</td>
        <td className="py-2.5 px-3 text-sm text-gray-800 max-w-[220px] truncate">{mov.concepto}</td>
        <td className="py-2.5 px-3 text-xs text-gray-500 whitespace-nowrap">
          {mov.categoria && CATEGORIAS[mov.categoria] ? CATEGORIAS[mov.categoria].label : '—'}
        </td>
        <td className={`py-2.5 px-3 text-sm font-bold text-right ${mov.importe >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {fmt(mov.importe)}
        </td>
        <td className="py-2.5 px-3 text-sm text-gray-400 text-right">{mov.saldo != null ? fmtAbs(mov.saldo) : '—'}</td>
        <td className="py-2.5 px-3">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ei.color}`}>{ei.label}</span>
        </td>
        <td className="py-2.5 px-3 text-xs text-gray-400">{mov.facturaRef || '—'}</td>
        <td className="py-2.5 px-3 text-gray-400 text-sm">{expanded ? '▲' : '▼'}</td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50 border-b border-gray-100">
          <td colSpan={8} className="px-4 py-4">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Auto-match */}
              {facturasMatch.length > 0 && !mov.conciliado && (
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                    Factura sugerida
                  </label>
                  <select
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={selectedFactura}
                    onChange={e => setSelectedFactura(e.target.value)}
                  >
                    <option value="">— Seleccionar factura —</option>
                    {facturasMatch.map(({ factura: f, dias, exacto, motivo }) => (
                      <option key={f.id} value={f.id}>
                        {f.referencia || f.id} · {f.clienteNombre} · {fmtAbs(f.importeTotal || f.importe)}
                        {exacto ? ' · importe exacto' : ''}
                        {dias < 90 ? ` · ${Math.round(dias)}d` : ''}
                        {' · '}por {MOTIVO_LABEL[motivo]}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Búsqueda manual */}
              {!mov.conciliado && (
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                    O buscar factura manualmente
                  </label>
                  <select
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={selectedFactura}
                    onChange={e => setSelectedFactura(e.target.value)}
                  >
                    <option value="">— Todas las facturas —</option>
                    {facturas.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.referencia || f.id} · {f.clienteNombre || '—'} · {fmtAbs(f.importeTotal || f.importe)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-2">
                {!mov.conciliado ? (
                  <button
                    onClick={() => { if (selectedFactura) onConciliar(mov, selectedFactura, facturas.find(f => f.id === selectedFactura)); }}
                    disabled={!selectedFactura}
                    className="px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-40"
                  >
                    ✓ Conciliar
                  </button>
                ) : (
                  <button
                    onClick={() => onConciliar(mov, null, null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200"
                  >
                    Deshacer conciliación
                  </button>
                )}
                <button onClick={() => setExpanded(false)} className="px-4 py-2 border border-gray-200 text-xs rounded-lg hover:bg-gray-50">
                  Cerrar
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────

export default function BancosView() {
  const [movimientos, setMovimientos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [tab, setTab] = useState('dashboard'); // 'dashboard' | 'movimientos' | 'importar'
  const [formatoImport, setFormatoImport] = useState('csv-santander'); // 'csv-santander' | 'pdf' | 'csv-otro'
  const [search, setSearch] = useState('');
  const [filtroConc, setFiltroConc] = useState(''); // '' | 'conciliado' | 'no_conciliado'
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [error, setError] = useState('');
  const [importInfo, setImportInfo] = useState('');

  // Cargar movimientos de Firestore
  const cargarMovimientos = useCallback(() => {
    return getDocs(query(collection(db, 'movimientos_banco'), orderBy('fecha', 'desc')))
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })))
      .catch(() => []);
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      cargarMovimientos(),
      getDocs(query(collection(db, 'facturas'), orderBy('fecha', 'desc'))).then(snap =>
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
      ).catch(() => []),
    ]).then(([movs, facts]) => {
      setMovimientos(movs);
      setFacturas(facts);
      setLoading(false);
    });
  }, [cargarMovimientos]);

  const handleImport = async (movs) => {
    setImporting(true);
    setError('');
    setImportInfo('');
    try {
      // Evitar duplicados: comparar fecha + importe + saldo + concepto contra
      // TODO lo ya importado. El saldo (balance acumulado tras el movimiento)
      // es importante — sin él, dos cargos idénticos el mismo día por el
      // mismo importe y con el mismo concepto (pasa de verdad, p.ej. varios
      // peajes seguidos de "Pago Movil En Autopista...") se confundirían
      // entre sí; con el saldo cada uno queda identificado sin ambigüedad,
      // porque el saldo acumulado nunca coincide entre dos movimientos
      // distintos aunque el resto de datos sean iguales.
      const claveDe = (m) => `${m.fecha}_${m.importe}_${m.saldo}_${m.concepto}`;
      const existing = new Set(movimientos.map(claveDe));
      const nuevos = movs.filter(m => !existing.has(claveDe(m)));
      const duplicados = movs.length - nuevos.length;

      if (nuevos.length === 0) {
        setError('Todos los movimientos del archivo ya están importados (sin duplicados nuevos).');
        setImporting(false);
        return;
      }

      // Categorización automática (nóminas, Seguridad Social, TPV, proveedores...)
      // — así el dashboard puede desglosar sin que haya que clasificar nada a mano.
      const nuevosCategorizados = nuevos.map(m => {
        const cat = categorizeMovimiento(m.concepto, m.importe);
        return { ...m, categoria: cat.categoria };
      });

      // Guardar en lotes
      const batch = nuevosCategorizados.map(m => addDoc(collection(db, 'movimientos_banco'), {
        ...m,
        importadoAt: new Date().toISOString(),
      }));
      const refs = await Promise.all(batch);

      const saved = nuevosCategorizados.map((m, i) => ({ id: refs[i].id, ...m }));
      // Reordenar por fecha desc — el dashboard asume que movimientos[0] es el
      // más reciente para mostrar el saldo actual.
      setMovimientos(prev => [...saved, ...prev].sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
      if (duplicados > 0) {
        setImportInfo(`${saved.length} movimientos nuevos importados — ${duplicados} ya estaban importados y se han descartado para no duplicar.`);
      }
      setTab('movimientos');
    } catch (e) {
      setError(`Error al importar: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Antes escribía solo en movimientos_banco desde el cliente — la factura
  // no se enteraba de nada, así que "conciliado" en Bancos y "cobrada" en
  // Facturas podían quedar contradichos. conciliarPago.js actualiza los dos
  // lados a la vez (ver ese archivo para la lógica exacta de desconciliar).
  const handleConciliar = async (mov, facturaId, factura) => {
    try {
      const resp = await fetch('/.netlify/functions/conciliarPago', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ movimientoId: mov.id, facturaId: facturaId || null }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');

      setMovimientos(prev => prev.map(m =>
        m.id === mov.id
          ? { ...m, conciliado: !!facturaId, facturaId: facturaId || null, facturaRef: factura ? (factura.referencia || factura.id) : null }
          : m
      ));
      if (facturaId && factura) {
        setFacturas(prev => prev.map(f => f.id === facturaId ? { ...f, conciliada: true, estado: 'cobrada' } : f));
      }
    } catch (e) {
      setError(`Error al conciliar: ${e.message}`);
    }
  };

  const filtered = useMemo(() => {
    let list = movimientos;
    if (filtroConc === 'conciliado') list = list.filter(m => m.conciliado);
    if (filtroConc === 'no_conciliado') list = list.filter(m => !m.conciliado);
    if (fechaDesde) list = list.filter(m => m.fecha >= fechaDesde);
    if (fechaHasta) list = list.filter(m => m.fecha <= fechaHasta);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(m =>
        (m.concepto || '').toLowerCase().includes(s) ||
        (m.facturaRef || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [movimientos, search, filtroConc, fechaDesde, fechaHasta]);

  const stats = useMemo(() => {
    const total = movimientos.length;
    const conciliados = movimientos.filter(m => m.conciliado).length;
    const entradas = movimientos.filter(m => m.importe > 0).reduce((s, m) => s + m.importe, 0);
    const salidas = movimientos.filter(m => m.importe < 0).reduce((s, m) => s + m.importe, 0);
    return { total, conciliados, pendientes: total - conciliados, entradas, salidas };
  }, [movimientos]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Bancos</h2>
            <p className="text-gray-500 text-sm mt-1">
              Dashboard financiero, importación de extractos y conciliación con facturas
            </p>
          </div>
          <button
            onClick={() => setTab('importar')}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
          >
            <span>⬆</span> Importar extracto
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 border-b border-gray-100">
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'movimientos', label: `Movimientos (${movimientos.length})` },
            { id: 'importar', label: 'Importar extracto' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.id ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error global */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="ml-4 font-bold text-red-500">×</button>
        </div>
      )}

      {/* Aviso informativo (p.ej. duplicados descartados en la última importación) */}
      {importInfo && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-xl px-4 py-3 flex items-center justify-between">
          {importInfo}
          <button onClick={() => setImportInfo('')} className="ml-4 font-bold text-blue-500">×</button>
        </div>
      )}

      {tab === 'dashboard' ? (
        loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : (
          <BancoDashboard movimientos={movimientos} />
        )
      ) : tab === 'importar' ? (
        <div className="bg-white rounded-2xl shadow-lg border p-6">
          <h3 className="font-bold text-gray-800 mb-4">Importar extracto bancario</h3>
          {importing ? (
            <div className="flex flex-col items-center py-12 gap-4">
              <Spinner />
              <p className="text-gray-500 text-sm">Importando movimientos…</p>
            </div>
          ) : (
            <>
              <div className="flex gap-1 mb-4">
                {[['csv-santander', 'CSV (Santander)'], ['pdf', 'PDF (Santander)'], ['csv-otro', 'Otro banco (CSV)']].map(([id, label]) => (
                  <button key={id} onClick={() => setFormatoImport(id)}
                    className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                      formatoImport === id ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              {formatoImport === 'csv-santander' ? (
                <ImportSantanderCSVPanel onImport={handleImport} />
              ) : formatoImport === 'pdf' ? (
                <ImportPDFPanel onImport={handleImport} />
              ) : (
                <ImportCSVPanel onImport={handleImport} />
              )}
            </>
          )}
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { label: 'Total movimientos', val: stats.total, color: 'text-gray-800' },
              { label: 'Conciliados', val: stats.conciliados, color: 'text-green-600' },
              { label: 'Sin conciliar', val: stats.pendientes, color: 'text-orange-600' },
              { label: 'Total entradas', val: fmtAbs(stats.entradas), color: 'text-green-600' },
              { label: 'Total salidas', val: fmtAbs(Math.abs(stats.salidas)), color: 'text-red-600' },
            ].map(k => (
              <div key={k.label} className="bg-white rounded-xl shadow border p-4 text-center">
                <div className={`text-xl font-bold ${k.color}`}>{k.val}</div>
                <div className="text-xs text-gray-500 mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Lista de movimientos */}
          <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-4">
            {/* Filtros */}
            <div className="flex flex-wrap gap-3">
              <input
                className="flex-1 min-w-[200px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                placeholder="Buscar por concepto o factura…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                value={filtroConc}
                onChange={e => setFiltroConc(e.target.value)}
              >
                <option value="">Todos los movimientos</option>
                <option value="conciliado">Solo conciliados</option>
                <option value="no_conciliado">Sin conciliar</option>
              </select>
              <div className="flex items-center gap-1.5">
                <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-red-400" />
                <span className="text-xs text-gray-400">a</span>
                <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-red-400" />
                {(fechaDesde || fechaHasta) && (
                  <button onClick={() => { setFechaDesde(''); setFechaHasta(''); }}
                    className="text-xs text-gray-400 hover:text-gray-600 px-1">×</button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Spinner /></div>
            ) : movimientos.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-5xl mb-3">🏦</div>
                <p className="text-gray-500 font-medium">No hay movimientos importados</p>
                <p className="text-gray-400 text-sm mt-1">Importa el extracto de tu banco (PDF o CSV) para empezar</p>
                <button
                  onClick={() => setTab('importar')}
                  className="mt-4 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
                >
                  Importar extracto
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {['Fecha', 'Concepto', 'Categoría', 'Importe', 'Saldo', 'Estado', 'Factura', ''].map(h => (
                        <th key={h} className={`py-2 px-3 text-xs font-bold text-gray-400 uppercase ${h === 'Importe' || h === 'Saldo' ? 'text-right' : 'text-left'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(m => (
                      <MovRow
                        key={m.id}
                        mov={m}
                        facturas={facturas}
                        onConciliar={handleConciliar}
                      />
                    ))}
                  </tbody>
                </table>

                {filtered.length === 0 && (
                  <p className="text-center text-gray-400 py-8">Sin resultados para los filtros aplicados.</p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
